// public/src/pages/NewsArticle.jsx
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FiCalendar, 
  FiUser, 
  FiArrowLeft, 
  FiShare2, 
  FiExternalLink,
  FiFacebook,
  FiTwitter,
  FiLinkedin,
  FiLink,
  FiBookmark
} from 'react-icons/fi';
import axios from 'axios';

function NewsArticle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [latestNews, setLatestNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchNewsArticle();
  }, [id]);

  const fetchNewsArticle = async () => {
    try {
      // Try to fetch by slug first, then by ID
      const response = await axios.get(`https://agri-back-mauve.vercel.app/api/content/public/news`);
      const allNews = response.data.data;
      
      // Find the article by slug or ID
      const foundArticle = allNews.find(a => 
        a.seo?.slug === id || a._id === id
      );

      if (foundArticle) {
        setArticle(foundArticle);
        
        // Fetch latest news (excluding current article)
        const latest = allNews
          .filter(a => a._id !== foundArticle._id)
          .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt))
          .slice(0, 3);
        setLatestNews(latest);

        // Increment view count
        await incrementViewCount(foundArticle._id);
      } else {
        setError('News article not found');
      }
    } catch (err) {
      setError('Failed to load news article');
      console.error('Error fetching news article:', err);
    } finally {
      setLoading(false);
    }
  };

  const incrementViewCount = async (articleId) => {
    try {
      await axios.put(`https://agri-back-mauve.vercel.app/api/content/admin/${articleId}/views`);
    } catch (err) {
      console.error('Error incrementing view count:', err);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const shareOnSocialMedia = (platform) => {
    const url = window.location.href;
    const title = article?.title || '';
    const text = article?.description || '';

    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
    };

    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p>Loading news article...</p>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">News Article Not Found</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Link to="/news" className="text-blue-600 hover:text-blue-700 font-medium">
            Back to News
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="news-article-page">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200">
        <div className="container">
          <div className="flex items-center justify-between py-4">
            <button
              onClick={() => navigate('/news')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <FiArrowLeft size={20} />
              <span>Back to News</span>
            </button>
            
            <div className="flex items-center gap-4">
              {/* Share buttons */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => shareOnSocialMedia('facebook')}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                  title="Share on Facebook"
                >
                  <FiFacebook size={18} />
                </button>
                <button
                  onClick={() => shareOnSocialMedia('twitter')}
                  className="p-2 text-blue-400 hover:bg-blue-50 rounded-full transition-colors"
                  title="Share on Twitter"
                >
                  <FiTwitter size={18} />
                </button>
                <button
                  onClick={() => shareOnSocialMedia('linkedin')}
                  className="p-2 text-blue-700 hover:bg-blue-50 rounded-full transition-colors"
                  title="Share on LinkedIn"
                >
                  <FiLinkedin size={18} />
                </button>
                <button
                  onClick={copyToClipboard}
                  className="p-2 text-gray-600 hover:bg-gray-50 rounded-full transition-colors relative"
                  title="Copy link"
                >
                  <FiLink size={18} />
                  {copied && (
                    <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded">
                      Copied!
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Article Header */}
      <article className="bg-white">
        <div className="container">
          {/* Featured Image */}
          {article.image && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8 rounded-xl overflow-hidden"
            >
              <img
                src={article.image.startsWith('http') ? article.image : `https://agri-back-mauve.vercel.app${article.image}`}
                alt={article.title}
                className="w-full h-96 object-cover"
              />
            </motion.div>
          )}

          {/* Article Header Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="max-w-4xl mx-auto mb-12"
          >
            {/* Featured Badge */}
            {article.featured && (
              <span className="inline-block bg-yellow-100 text-yellow-800 text-sm font-medium px-4 py-2 rounded-full mb-6">
                Featured News
              </span>
            )}

            {/* Title */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              {article.title}
            </h1>

            {/* Subtitle */}
            {article.subtitle && (
              <p className="text-xl md:text-2xl text-gray-600 mb-8 leading-relaxed">
                {article.subtitle}
              </p>
            )}

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 text-gray-500 mb-8 border-t border-b border-gray-200 py-4">
              <div className="flex items-center gap-2">
                <FiUser size={16} />
                <span>{article.author || 'Afrikonsults'}</span>
              </div>
              <div className="flex items-center gap-2">
                <FiCalendar size={16} />
                <span>{formatDate(article.publishedAt)}</span>
              </div>
              {article.views > 0 && (
                <div className="flex items-center gap-2">
                  <span>{article.views} views</span>
                </div>
              )}
            </div>

            {/* Summary/Description */}
            {article.description && (
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg mb-8">
                <p className="text-lg text-gray-700 leading-relaxed">
                  {article.description}
                </p>
              </div>
            )}
          </motion.div>
        </div>
      </article>

      {/* Article Content */}
      <section className="bg-gray-50 py-12">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl shadow-lg p-8 md:p-12"
            >
              {/* Article Content */}
              <div className="prose prose-lg max-w-none">
                {article.content ? (
                  <div 
                    className="news-content"
                    dangerouslySetInnerHTML={{ __html: article.content }}
                  />
                ) : (
                  <div className="text-gray-600 leading-relaxed space-y-6">
                    <p className="text-xl text-gray-700">
                      In a significant development for African agriculture, new technological advancements 
                      are paving the way for unprecedented growth and sustainability in the sector.
                    </p>
                    
                    <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
                      Breaking New Ground in Agricultural Innovation
                    </h2>
                    
                    <p>
                      The recent developments mark a turning point for farmers across the continent, 
                      offering new opportunities for increased productivity and improved livelihoods.
                    </p>
                    
                    <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                      Key Developments Include:
                    </h3>
                    
                    <ul className="list-disc list-inside space-y-2 text-gray-600">
                      <li>Implementation of advanced precision farming techniques</li>
                      <li>Expansion of IoT-based monitoring systems</li>
                      <li>New partnerships with international agricultural organizations</li>
                      <li>Government initiatives supporting technology adoption</li>
                      <li>Breakthrough research in crop optimization</li>
                    </ul>
                    
                    <div className="bg-gray-100 p-6 rounded-lg my-6">
                      <h4 className="font-semibold text-gray-900 mb-2">Official Statement</h4>
                      <p className="text-gray-700 italic">
                        "This represents a major step forward in our mission to transform African agriculture 
                        through innovative technology solutions. We are committed to empowering farmers with 
                        the tools they need to succeed in today's competitive landscape."
                      </p>
                      <p className="text-gray-600 text-sm mt-2">
                        — Afrikonsults Leadership Team
                      </p>
                    </div>
                    
                    <p>
                      The impact of these developments is already being felt across multiple African nations, 
                      with early adopters reporting significant improvements in crop yields and operational 
                      efficiency.
                    </p>
                    
                    <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                      Looking Ahead
                    </h3>
                    
                    <p>
                      As the agricultural technology sector continues to evolve, experts predict even greater 
                      advancements in the coming years, with a focus on sustainable practices and climate-resilient 
                      farming methods.
                    </p>
                    
                    <blockquote className="border-l-4 border-blue-500 pl-4 italic text-gray-700 my-6">
                      "The future of African agriculture is digital, and we're just beginning to scratch 
                      the surface of what's possible with today's technology."
                    </blockquote>
                  </div>
                )}
              </div>

              {/* Press Contact */}
              <div className="mt-12 p-6 bg-blue-50 rounded-xl border border-blue-200">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Media Inquiries
                </h3>
                <p className="text-gray-600 mb-4">
                  For more information about this announcement or to schedule an interview with our experts, 
                  please contact our media relations team.
                </p>
                <div className="flex flex-wrap gap-4">
                  <a
                    href="/contact"
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                  >
                    Contact Media Team
                  </a>
                  <a
                    href="/press-kit"
                    className="border border-blue-600 text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
                  >
                    Download Press Kit
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Latest News */}
      {latestNews.length > 0 && (
        <section className="py-12 bg-white">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <h2 className="text-3xl font-bold text-gray-900 text-center mb-2">
                Latest News
              </h2>
              <p className="text-gray-600 text-center mb-8">
                Stay updated with the latest developments
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {latestNews.map((newsItem, index) => (
                  <motion.article
                    key={newsItem._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 * index }}
                    className="bg-gray-50 rounded-xl overflow-hidden hover:shadow-lg transition-shadow duration-300"
                  >
                    {newsItem.image && (
                      <div className="h-48 overflow-hidden">
                        <img
                          src={newsItem.image.startsWith('http') ? newsItem.image : `https://agri-back-mauve.vercel.app${newsItem.image}`}
                          alt={newsItem.title}
                          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                        />
                      </div>
                    )}
                    
                    <div className="p-6">
                      {newsItem.featured && (
                        <span className="inline-block bg-yellow-100 text-yellow-800 text-xs font-medium px-2 py-1 rounded-full mb-3">
                          Featured
                        </span>
                      )}
                      
                      <h3 className="font-bold text-lg text-gray-900 mb-3 line-clamp-2">
                        {newsItem.title}
                      </h3>
                      
                      {newsItem.subtitle && (
                        <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                          {newsItem.subtitle}
                        </p>
                      )}
                      
                      <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                        <span>{formatDate(newsItem.publishedAt)}</span>
                      </div>
                      
                      <Link
                        to={`/news/${newsItem.seo?.slug || newsItem._id}`}
                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium text-sm"
                      >
                        Read Full Story <FiExternalLink size={14} />
                      </Link>
                    </div>
                  </motion.article>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Press Kit CTA */}
      <section className="py-12 bg-gray-900">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center text-white"
          >
            <h2 className="text-3xl font-bold mb-4">Media Resources</h2>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              Access our complete media kit, including company information, high-resolution images, 
              and brand assets for your publications.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/press-kit"
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Download Media Kit
              </a>
              <a
                href="/contact"
                className="border border-gray-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors"
              >
                Request Interview
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default NewsArticle;